/**
 * The home page's sections, as data.
 *
 * The landing page is going to grow — synthetic data, training runs, calibration — and the way
 * that goes wrong is a component that accretes another `{#if}` branch and another 200 lines of
 * prose per phase until nobody can find the number they came for. So the sections are a table:
 * adding one is an entry here plus the panel component it names, and the rail, the routing, the
 * roving `tabindex` and the deep link all follow from it.
 *
 * **No placeholder entries.** A "Model" tab promising something that does not exist yet is worse
 * than no tab: it teaches the operator that this page describes intentions rather than the
 * system. A section appears here when it has something true to show.
 *
 * This module deliberately holds **no Svelte imports**. The router needs the tab ids to parse
 * `#/home/<tab>`, and the router is unit-tested in a plain node environment; pulling the panel
 * components (and through them Konva, and through Konva a native canvas binding) into the
 * router's module graph to look up four strings would be a bad trade. The panels are bound to
 * these ids in `components/HomePage.svelte`, in a `Record<HomeTabId, Component>` so that adding
 * an id here without a panel is a **compile error**, not a blank tab.
 */

export interface HomeTabMeta {
  /** Stable id. It is in the URL (`#/home/<id>`), so treat it as a public name. */
  id: string;
  /** Rail label. Short: the rail is a column, not a menu bar. */
  label: string;
  /** One line under the label, and the tab's accessible description. */
  description: string;
}

/**
 * Order is rail order, and the **first entry is the default** — the view somebody lands on with
 * a bare `#/`.
 *
 * That is `start`, not `dashboard` *(changed 2026-08-09)*. The router's rationale for landing on
 * home at all is that somebody at the bare URL "is far more likely to be new than to be
 * resuming" — and that argument is right, but the dashboard did not serve it. A newcomer landed
 * on seven panels, six tables, a thirty-row per-class table and two SHA-256 hashes, every figure
 * reading `0` because nothing has been boxed yet. That is the densest screen in the product
 * answering a question the newcomer has not got yet, which is what users meant by
 * "overwhelming" and "hard to know what to do next".
 *
 * `start` answers the newcomer's question and carries a resolved call to action for the
 * returner, so one screen serves both. The numbers have not gone anywhere — the dashboard is one
 * arrow key away, which is where the newcomer's questions used to be.
 */
export const HOME_TABS = [
  {
    id: 'start',
    label: 'Getting started',
    description: 'The annotator id, the draw → assign → advance loop, and what to open first.',
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    description: 'Where the project is right now — progress, splits, per-class coverage.',
  },
  {
    id: 'conventions',
    label: 'Conventions',
    description: 'The three things people get wrong, with the worked examples that show them.',
  },
  {
    id: 'alphabet',
    label: 'Alphabet',
    description: 'The letter forms, with flashcards — learn the script the boxes are drawn around.',
  },
  {
    id: 'keyboard',
    label: 'Keyboard',
    description: 'Every shortcut, generated from the same table the workspace matches against.',
  },
] as const satisfies readonly HomeTabMeta[];

export type HomeTabId = (typeof HOME_TABS)[number]['id'];

export const DEFAULT_HOME_TAB: HomeTabId = HOME_TABS[0].id;

export const HOME_TAB_IDS: readonly HomeTabId[] = HOME_TABS.map((tab) => tab.id);

/**
 * Resolve a URL segment to a tab.
 *
 * An unrecognised id falls back to the default tab rather than rendering an empty panel: a stale
 * bookmark or a typo should land somewhere useful, and a blank page is indistinguishable from a
 * broken build.
 */
export function asHomeTab(value: string | undefined | null): HomeTabId {
  return HOME_TAB_IDS.includes(value as HomeTabId) ? (value as HomeTabId) : DEFAULT_HOME_TAB;
}

export function homeTabMeta(id: HomeTabId): HomeTabMeta {
  return HOME_TABS.find((tab) => tab.id === id) ?? HOME_TABS[0];
}
